import { useCallback, useRef, useState } from "react"

/**
 * localStorage key for the mobile table-screen coach-mark (decision #5). The guest flow has no auth,
 * so localStorage is the "per user" proxy: once dismissed, the coach-mark never auto-shows again.
 */
export const TABLE_COACHMARK_STORAGE_KEY = "tabb.publicBooking.tableCoachmarkSeen"

// SSR / disabled-storage safe: never throw if `window`/`localStorage` is missing or blocked
// (private mode, quota, security policy). Reading fails closed to "not seen" so the coach-mark still
// appears; writing fails silently so it simply reappears next session instead of crashing.
const hasSeenCoachmark = (): boolean => {
  if (typeof window === "undefined") return false
  try {
    return window.localStorage.getItem(TABLE_COACHMARK_STORAGE_KEY) === "true"
  } catch {
    return false
  }
}

const persistCoachmarkSeen = (): void => {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(TABLE_COACHMARK_STORAGE_KEY, "true")
  } catch {
    // Storage unavailable — ignore; the coach-mark will show again next session.
  }
}

/**
 * Mobile-only replacement for `useOnboardingTooltips` that shows the coach-mark once per user and
 * persists the dismissal in `localStorage`. The shared desktop `useOnboardingTooltips` is left
 * untouched (it stays always-on for its consumers).
 */
export const usePersistedCoachmark = (): {
  coachmarkVisible: boolean
  dismissCoachmark: () => void
} => {
  const [coachmarkVisible, setCoachmarkVisible] = useState(() => !hasSeenCoachmark())
  const dismissedRef = useRef(false)

  // Idempotent: safe to call on every screen interaction (see index.tsx capture handlers) — it only
  // writes to storage once, so repeated taps after the first don't hammer localStorage.
  const dismissCoachmark = useCallback(() => {
    if (dismissedRef.current) return
    dismissedRef.current = true
    setCoachmarkVisible(false)
    persistCoachmarkSeen()
  }, [])

  return { coachmarkVisible, dismissCoachmark }
}
